import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { TutorialCard } from '../components/TutorialCard';
import { SearchIcon, LayoutGridIcon, FileTextIcon, UsersIcon, ArrowLeftRightIcon, ShoppingBagIcon, SettingsIcon, BarChartIcon, CalendarIcon, TerminalIcon, SmartphoneIcon, BanknoteIcon, GlobeIcon } from 'lucide-react';
// Mock tutorial data
const tutorialData = [{
  id: 1,
  title: 'Getting Started with Payment Requests',
  description: 'Learn how to create and manage payment requests for your customers.',
  category: 'Basics',
  duration: '5 min',
  completed: true,
  completedAt: '2023-11-15T14:30:00',
  icon: <LayoutGridIcon size={24} className="text-blue-500" data-id="element-15108" data-mp="3ey2quvgvaby3" />
}, {
  id: 2,
  title: 'Setting Up Your Virtual Terminal',
  description: 'Configure your virtual terminal for accepting card payments.',
  category: 'Terminals',
  duration: '8 min',
  completed: true,
  completedAt: '2023-11-20T10:15:00',
  icon: <BanknoteIcon size={24} className="text-purple-500" data-id="element-15109" data-mp="2rvjm1q0ztw8g" />
}, {
  id: 3,
  title: 'Creating Your First Invoice',
  description: 'Learn how to create, customize and send professional invoices.',
  category: 'Invoicing',
  duration: '6 min',
  completed: false,
  completedAt: null,
  icon: <FileTextIcon size={24} className="text-green-500" data-id="element-15110" data-mp="2vx4xd6xvq2gj" />
}, {
  id: 4,
  title: 'Managing Customer Profiles',
  description: 'Organize and manage your customer information efficiently.',
  category: 'Customers',
  duration: '4 min',
  completed: true,
  completedAt: '2023-12-01T09:45:00',
  icon: <UsersIcon size={24} className="text-orange-700" aria-hidden="true" data-id="element-15111" data-mp="3jmpseo7b4q66" />
}, {
  id: 5,
  title: 'Understanding Transaction Reports',
  description: 'Learn how to view and interpret your transaction reports.',
  category: 'Reports',
  duration: '10 min',
  completed: false,
  completedAt: null,
  icon: <BarChartIcon size={24} className="text-red-500" data-id="element-15112" data-mp="3kcnzequzpura" />
}, {
  id: 6,
  title: 'Setting Up Physical Terminals',
  description: 'Configure and manage your physical POS terminals.',
  category: 'Terminals',
  duration: '12 min',
  completed: false,
  completedAt: null,
  icon: <TerminalIcon size={24} className="text-indigo-500" data-id="element-15113" data-mp="3ud6p4298k5h4" />
}, {
  id: 7,
  title: 'Configuring SoftPOS on Mobile Devices',
  description: 'Turn your smartphone into a payment terminal with SoftPOS.',
  category: 'Terminals',
  duration: '7 min',
  completed: false,
  completedAt: null,
  icon: <SmartphoneIcon size={24} className="text-teal-500" data-id="element-15114" data-mp="gvxmds0zqhg3" />
}, {
  id: 8,
  title: 'Connecting Third-Party Applications',
  description: 'Integrate your payment system with other business tools.',
  category: 'Integration',
  duration: '15 min',
  completed: true,
  completedAt: '2023-12-10T16:20:00',
  icon: <GlobeIcon size={24} className="text-blue-600" data-id="element-15115" data-mp="uuxej9nppeh0" />
}, {
  id: 9,
  title: 'Setting Up Booking Services',
  description: 'Configure your booking system for appointments and reservations.',
  category: 'Bookings',
  duration: '9 min',
  completed: false,
  completedAt: null,
  icon: <CalendarIcon size={24} className="text-pink-500" data-id="element-15116" data-mp="26e4c9rmkid11" />
}, {
  id: 10,
  title: 'Managing Your Online Shop',
  description: 'Learn how to set up and manage your online store.',
  category: 'E-commerce',
  duration: '14 min',
  completed: true,
  completedAt: '2023-12-05T11:30:00',
  icon: <ShoppingBagIcon size={24} className="text-yellow-600" data-id="element-15117" data-mp="2u3yn63nbtrin" />
}, {
  id: 11,
  title: 'Customizing Payment Settings',
  description: 'Configure payment settings to match your business needs.',
  category: 'Settings',
  duration: '6 min',
  completed: false,
  completedAt: null,
  icon: <SettingsIcon size={24} className="text-gray-600" data-id="element-15118" data-mp="3hszz40vdbkso" />
}, {
  id: 12,
  title: 'Advanced Transaction Management',
  description: 'Learn advanced techniques for managing complex transactions.',
  category: 'Advanced',
  duration: '18 min',
  completed: false,
  completedAt: null,
  icon: <ArrowLeftRightIcon size={24} className="text-purple-600" data-id="element-15119" data-mp="31mn2desdentw" />
}];
// Available categories for filtering
const categories = ['All', 'Basics', 'Terminals', 'Invoicing', 'Customers', 'Reports', 'Integration', 'Bookings', 'E-commerce', 'Settings', 'Advanced'];
export function Tutorials() {
  const {
    t
  } = useTranslation('tutorials');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('All');
  const [filter, setFilter] = useState('all'); // 'all', 'completed', 'incomplete'
  // Filter tutorials based on search, category, and completion status
  const filteredTutorials = tutorialData.filter(tutorial => {
    const matchesSearch = tutorial.title.toLowerCase().includes(searchTerm.toLowerCase()) || tutorial.description.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCategory = selectedCategory === 'All' || tutorial.category === selectedCategory;
    const matchesFilter = filter === 'all' || filter === 'completed' && tutorial.completed || filter === 'incomplete' && !tutorial.completed;
    return matchesSearch && matchesCategory && matchesFilter;
  });
  // Calculate progress statistics
  const totalTutorials = tutorialData.length;
  const completedTutorials = tutorialData.filter(t => t.completed).length;
  const progressPercentage = Math.round(completedTutorials / totalTutorials * 100);
  return <div className="space-y-6 max-w-7xl mx-auto" data-id="element-15120" data-mp="1imxa1i93pqd8">
      {/* Header */}
      <div className="flex justify-between items-center" data-id="element-15121" data-mp="1ik1dnycwj9qw">
        <h1 className="text-2xl font-semibold text-gray-900" data-id="element-15122" data-mp="1dmj88hy3fcnn">
          {t('pages.Tutorials.tutorials')}
        </h1>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4" data-id="element-15123" data-mp="2p2q16xnta49k">
        <div className="bg-white p-6 rounded-xl border border-gray-200 shadow-sm" data-id="element-15124" data-mp="2p4kebpsucs33">
          <div className="text-sm font-medium text-gray-500" data-id="element-15125" data-mp="1mojs6xefvw5s">
            {t('pages.Tutorials.total_tutorials')}
          </div>
          <div className="mt-2 text-2xl font-semibold text-gray-900" data-id="element-15126" data-mp="1rpzh50jqmmtg">
            {totalTutorials}
          </div>
        </div>
        <div className="bg-white p-6 rounded-xl border border-gray-200 shadow-sm" data-id="element-15127" data-mp="20pcptr01f7d4">
          <div className="text-sm font-medium text-gray-500" data-id="element-15128" data-mp="13yu1aldsruj5">
            {t('pages.Tutorials.completed_tutorials')}
          </div>
          <div className="mt-2 text-2xl font-semibold text-gray-900" data-id="element-15129" data-mp="18174p352ngcr">
            {completedTutorials}
          </div>
        </div>
        <div className="bg-white p-6 rounded-xl border border-gray-200 shadow-sm" data-id="element-15130" data-mp="1cxxljsopafz8">
          <div className="text-sm font-medium text-gray-500" data-id="element-15131" data-mp="1hrjouu8z57id">
            {t('pages.Tutorials.learning_progress')}
          </div>
          <div className="mt-2 text-2xl font-semibold text-gray-900" data-id="element-15132" data-mp="k8qm8rhf9mcx">
            {progressPercentage}%
          </div>
          <div className="w-full bg-gray-100 rounded-full h-1.5 mt-3" data-id="element-15133" data-mp="hpwo9k9n0l37">
            <div className="bg-blue-600 h-1.5 rounded-full transition-all duration-500 ease-out" style={{
            width: `${progressPercentage}%`
          }} data-id="element-15134" data-mp="ho2cavgcg3b9"></div>
          </div>
        </div>
      </div>

      {/* Main Content Card */}
      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden shadow-sm" data-id="element-15135" data-mp="3sghu8rccrbg">
        {/* Controls Bar */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between p-4 border-b border-gray-200 gap-4" data-id="element-15136" data-mp="3q5ylk237pm0q">
          {/* Status Filter Tabs */}
          <div className="flex space-x-6" data-id="element-15137" data-mp="3l7evpqlhzq33">
            <button onClick={() => setFilter('all')} className={`pb-4 -mb-4 text-sm font-medium transition-colors border-b-2 ${filter === 'all' ? 'border-blue-600 text-blue-600' : 'border-transparent text-gray-500 hover:text-gray-700'}`} data-id="element-15138" data-mp="3g7slzr4vorz1">
              {t('pages.Tutorials.all')}
            </button>
            <button onClick={() => setFilter('completed')} className={`pb-4 -mb-4 text-sm font-medium transition-colors border-b-2 ${filter === 'completed' ? 'border-blue-600 text-blue-600' : 'border-transparent text-gray-500 hover:text-gray-700'}`} data-id="element-15139" data-mp="yzpjlyg3f8av">
              {t('components.TutorialCard.completed')}
            </button>
            <button onClick={() => setFilter('incomplete')} className={`pb-4 -mb-4 text-sm font-medium transition-colors border-b-2 ${filter === 'incomplete' ? 'border-blue-600 text-blue-600' : 'border-transparent text-gray-500 hover:text-gray-700'}`} data-id="element-15140" data-mp="g86e9kylji5o">
              {t('pages.Tutorials.not_completed')}
            </button>
          </div>

          {/* Search */}
          <div className="relative w-64" data-id="element-15141" data-mp="3tdwvchlygh1x">
            <SearchIcon size={16} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" data-id="element-15142" data-mp="3tf8836xdoz56" />

            <input type="search" aria-label={t('pages.Tutorials.attrs.search_tutorials')} placeholder={t('pages.Tutorials.attrs.search_tutorials')} value={searchTerm} onChange={e => setSearchTerm(e.target.value)} className="w-full pl-9 pr-4 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-colors" data-id="element-15143" data-mp="3er7vufrpe3vj" />
          </div>
        </div>

        {/* Category Pills */}
        <div className="p-4 border-b border-gray-100 bg-gray-50/50 overflow-x-auto" data-id="element-15144" data-mp="3sr8v5qsbl38w">
          <div className="flex space-x-2" data-id="element-15145" data-mp="b9pr4h789czc">
            {categories.map(category => <button key={category} onClick={() => setSelectedCategory(category)} className={`px-3 py-1.5 rounded-full text-sm font-medium whitespace-nowrap transition-colors ${selectedCategory === category ? 'bg-gray-900 text-white' : 'bg-white text-gray-600 border border-gray-200 hover:bg-gray-50'}`} data-id="element-15146" data-mp="6czclyb2mpg1">
                {category}
              </button>)}
          </div>
        </div>

        {/* Tutorials grid */}
        <div className="p-6 bg-gray-50/30" data-id="element-15147" data-mp="39opykkfesicq">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-id="element-15148" data-mp="39lk4srvu4sao">
            {filteredTutorials.map(tutorial => <TutorialCard key={tutorial.id} id={tutorial.id} title={tutorial.title} description={tutorial.description} category={tutorial.category} duration={tutorial.duration} completed={tutorial.completed} completedAt={tutorial.completedAt} icon={tutorial.icon} data-id="element-15149" data-mp="67hgq0kolii0" />)}
            {filteredTutorials.length === 0 && <div className="col-span-1 md:col-span-2 lg:col-span-3 py-16 flex flex-col items-center justify-center text-center" data-id="element-15150" data-mp="2c061i3fq0x92">
                <div className="w-12 h-12 bg-gray-100 rounded-full flex items-center justify-center mb-4" data-id="element-15151" data-mp="rj6nof3bkn0l">
                  <SearchIcon size={24} className="text-gray-400" data-id="element-15152" data-mp="whq8gc66fsrd" />
                </div>
                <h3 className="text-sm font-medium text-gray-900" data-id="element-15153" data-mp="11f06gg7mtcqt">
                  {t('pages.Tutorials.no_tutorials_found')}
                </h3>
                <p className="text-sm text-gray-500 mt-1" data-id="element-15154" data-mp="168kv3hnc4udc">
                  {t('pages.Tutorials.try_adjusting_your_search_or_filters')}
                </p>
              </div>}
          </div>
        </div>
      </div>
    </div>;
}